// Victory screen: the six tracks as cards. Each card ranks nations by its own measure (your
// nation pinned and highlighted, the other playable leaders labelled), states that measure in
// its caption (rungs of 9, shares of world totals, or an index: see tracks.ts), explains its
// inputs and data years on hover, and charts 2000 → the viewed date from world.victorySeries(track).
// Score is the weighted mean of the five tracks with weights the player can edit.
//
// The standings paint first; the histories (~200 ms of world.victorySeries in all) fill in one
// track per idle slice, so opening the screen is not one long frame.

import { signal } from '@preact/signals';
import { Fragment, type JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import type { Services } from '../services/api.ts';
import type { VictoryEntry, VictoryTrack } from '../world/api.ts';
import { useServices } from './context.ts';
import { spaceLadderOf } from './extensions.ts';
import { isStale } from './format.ts';
import { Icon, type IconName } from './icons.tsx';
import { whenIdle } from './idle.ts';
import { ageOf, inclText, inclYears, partsOf } from './inputs.ts';
import { lookOf, selfIso } from './nation.ts';
import { ClaimLabel, Screen, SourceChip, useTip } from './parts.tsx';
import { BASE_TRACKS, DEFAULT_WEIGHTS, WEIGHT_MAX, loadWeights, saveWeights, scoreOf, type BaseTrack, type Weights } from './score.ts';
import { LADDER_RUNGS, TRACK_RULE, barFill, fmtTrack, pastSeries, rankAt, rungsOf, trackZero } from './tracks.ts';

type Place = ReturnType<typeof rankAt>;

const TOP_N = 5;
const CHART_W = 300;
const CHART_H = 84;
const CHART_HEADROOM = 1.12;
const GRID_YEARS = 5;
const TIP_SOURCES = 3;
const PART_SEP = ' · ';
const INCL_VAL_W = '124px';

const ALL_TRACKS: VictoryTrack[] = [...BASE_TRACKS, 'score'];

// Names per ARCHITECTURE §4; what each number measures is in TRACK_RULE (tracks.ts).
const TRACK_META: Record<VictoryTrack, { name: string; icon: IconName; color: string }> = {
  space: { name: 'Space race', icon: 'launch', color: '#8ec5ff' },
  tech: { name: 'Tech frontier', icon: 'science', color: '#5fb2ff' },
  economic: { name: 'Economic', icon: 'economy', color: '#f2c14e' },
  diplomatic: { name: 'Diplomatic', icon: 'diplomacy', color: '#7fd8c4' },
  culture: { name: 'Culture', icon: 'culture', color: '#c58cff' },
  score: { name: 'Score', icon: 'victory', color: '#e6c77a' },
};

const TRACK_NAMES = Object.fromEntries(BASE_TRACKS.map((t) => [t, TRACK_META[t].name])) as Record<BaseTrack, string>;

// Score weights live in score.ts; these signals hold the viewer's current choice.
const weights = signal(loadWeights());
const editingWeights = signal(false);

// ─── Data per track (current date + history) ─────────────────────────────────────────────

type Series = Array<{ year: number; entries: VictoryEntry[] }>;

interface TrackData {
  entries: VictoryEntry[];
  /** null while the history is still being computed. */
  series: Series | null;
}

type History = Partial<Record<BaseTrack, Series>>;

// What the world has already handed over, kept so reopening the screen paints the charts at once.
const historyCache = new WeakMap<object, History>();

/** The histories computed so far; the rest arrive one track per idle slice (world.victorySeries is cached per track). */
function useHistory(world: Services['world']): History {
  const [history, setHistory] = useState<History>(() => historyCache.get(world) ?? {});

  useEffect(() => {
    let cancel = () => {};
    const next = (): void => {
      const t = BASE_TRACKS.find((track) => !historyCache.get(world)?.[track]);
      if (!t) {
        return;
      }
      cancel = whenIdle(() => {
        const done = { ...historyCache.get(world), [t]: world.victorySeries(t) };
        historyCache.set(world, done);
        setHistory(done);
        next();
      });
    };
    next();
    return () => cancel();
  }, [world]);

  return history;
}

function trackData(world: Services['world'], date: string, history: History): Record<VictoryTrack, TrackData> {
  const w = weights.value;
  const base = {} as Record<BaseTrack, TrackData>;
  for (const t of BASE_TRACKS) {
    const entries = world.victory(t);
    const past = history[t];
    base[t] = { entries, series: past ? pastSeries(past, date, entries) : null };
  }

  const now = {} as Record<BaseTrack, VictoryEntry[]>;
  for (const t of BASE_TRACKS) {
    now[t] = base[t].entries;
  }
  return { ...base, score: { entries: scoreOf(now, w, TRACK_NAMES), series: scoreSeries(base, w) } };
}

/** Score history: the same weighted mean, year by year; null until every track's history is in. */
function scoreSeries(base: Record<BaseTrack, TrackData>, w: Weights): Series | null {
  const economic = base.economic.series;
  if (BASE_TRACKS.some((t) => base[t].series === null) || !economic) {
    return null;
  }

  return economic.map(({ year }, i) => {
    const perTrack = {} as Record<BaseTrack, VictoryEntry[]>;
    for (const t of BASE_TRACKS) {
      perTrack[t] = base[t].series?.[i]?.entries ?? [];
    }
    return { year, entries: scoreOf(perTrack, w, TRACK_NAMES) };
  });
}

// ─── Screen ──────────────────────────────────────────────────────────────────────────────

export function VictoryScreen() {
  const services = useServices();
  const { world, actions } = services;
  const history = useHistory(world);
  const self = selfIso(services);
  if (!self) {
    return null;
  }
  const rivals = world.perspectives().map((p) => p.iso3).filter((c) => c !== self);
  const data = trackData(world, services.state.date.value, history);

  return (
    <Screen
      title="Victory"
      icon="victory"
      class="wc-vic-screen"
      subtitle={
        <>
          Each card ranks by its own measure · <b>{lookOf(world, self).name}</b> pinned · <ClaimLabel kind="data" />
        </>
      }
      onClose={() => actions.openPanel('none')}
    >
      <div class="wc-vic-grid">
        {ALL_TRACKS.map((t) => (
          <TrackCard key={t} track={t} data={data[t]} self={self} rivals={rivals} />
        ))}
      </div>
    </Screen>
  );
}

interface CardProps {
  track: VictoryTrack;
  data: TrackData;
  self: string;
  rivals: string[];
}

function TrackCard({ track, data, self, rivals }: CardProps) {
  const { state } = useServices();
  const meta = TRACK_META[track];
  const { entries } = data;
  const selfPos = entries.findIndex((e) => e.iso3 === self);

  // Top N, then pinned rows for you and the rivals when they fall below.
  const rows: Array<{ e: VictoryEntry; place: Place }> = entries.slice(0, TOP_N).map((e, i) => ({ e, place: rankAt(entries, i) }));
  const pinned = [self, ...rivals]
    .map((iso3) => entries.findIndex((e) => e.iso3 === iso3))
    .filter((i) => i >= TOP_N)
    .sort((a, b) => a - b)
    .map((i) => ({ e: entries[i], place: rankAt(entries, i) }));
  const max = entries[0]?.progress ?? 1;

  // One value column width per card, so every bar in it keeps the same length scale.
  const at = state.date.value;
  const wide = [...rows, ...pinned].some((r) => ageOf(r.e, at).stale.length > 0);

  return (
    <article class="wc-vic-card" style={{ '--tc': meta.color }}>
      <header class="wc-vic-head">
        <span class="wc-vic-icon">
          <Icon name={meta.icon} size={20} />
        </span>
        <div class="wc-vic-titles">
          <h2 class="wc-vic-name">{meta.name}</h2>
          <div class="wc-vic-inputs" title={TRACK_RULE[track].note}>
            {TRACK_RULE[track].formula}
          </div>
        </div>
        {track === 'score' ? (
          <button type="button" class="wc-btn wc-vic-weights" aria-pressed={editingWeights.value} onClick={() => (editingWeights.value = !editingWeights.value)}>
            <Icon name="scales" size={13} />
            {editingWeights.value ? 'Done' : 'Weights'}
          </button>
        ) : null}
        <div class="wc-vic-rank">
          {selfPos >= 0 ? (
            <>
              <span class="wc-vic-rank-pos wc-num">#{rankAt(entries, selfPos).pos}</span>
              <span class="wc-vic-rank-of">
                of {entries.length}
                {rankAt(entries, selfPos).tied ? ' · tied' : ''}
              </span>
            </>
          ) : (
            <span class="wc-vic-rank-of">unranked</span>
          )}
        </div>
      </header>

      {track === 'score' && editingWeights.value ? (
        <WeightsEditor />
      ) : (
        <>
          {track === 'space' ? <SpaceNext self={self} entries={entries} /> : null}
          <div class="wc-vic-bars" style={wide ? { '--val-w': INCL_VAL_W } : undefined}>
            {entries.length === 0 ? <div class="wc-empty">No data for this track</div> : null}
            {rows.map((r) => (
              <BarRow key={r.e.iso3} track={track} entry={r.e} place={r.place} max={max} self={self} rivals={rivals} />
            ))}
            {pinned.length > 0 ? <div class="wc-vic-gap">⋯</div> : null}
            {pinned.map((r) => (
              <BarRow key={r.e.iso3} track={track} entry={r.e} place={r.place} max={max} self={self} rivals={rivals} />
            ))}
          </div>
          <HistoryChart track={track} data={data} self={self} rivals={rivals} color={meta.color} />
        </>
      )}

    </article>
  );
}

/** "USA 7/9 · next: crewed lunar landing (21st century)": where your nation stands on the ladder. */
function SpaceNext({ self, entries }: { self: string; entries: VictoryEntry[] }) {
  const { world } = useServices();
  const ladder = spaceLadderOf(world, self);
  const mine = entries.find((e) => e.iso3 === self);
  const reached = ladder?.reached ?? (mine ? rungsOf(mine.progress) : null);
  if (reached === null) {
    return null;
  }

  const total = ladder?.total ?? LADDER_RUNGS;
  let next = ladder ? 'all rungs reached' : '';
  if (ladder?.next) {
    next = `next: ${ladder.next.label}`;
  }
  return (
    <div class="wc-vic-next">
      <b class="wc-num">
        {self} {reached}/{total}
      </b>
      {next ? <span>{next}</span> : null}
    </div>
  );
}

interface BarProps {
  track: VictoryTrack;
  entry: VictoryEntry;
  place: Place;
  max: number;
  self: string;
  rivals: string[];
}

function BarRow({ track, entry, place, max, self, rivals }: BarProps) {
  const { world, state } = useServices();
  const look = lookOf(world, entry.iso3);
  const isSelf = entry.iso3 === self;
  const isRival = rivals.includes(entry.iso3);
  const age = ageOf(entry, state.date.value);
  const handlers = useTip(() => <BarTip track={track} entry={entry} name={look.name} />, 'right');

  const cls = ['wc-bar', isSelf ? 'wc-bar--self' : '', isRival ? 'wc-bar--rival' : '', age.grey ? 'wc-bar--stale' : ''].join(' ');
  return (
    <div class={cls} tabIndex={0} {...handlers}>
      <span class="wc-bar-pos wc-num">
        {place.tied ? '=' : ''}
        {place.pos}
      </span>
      <span class="wc-swatch" style={{ background: look.colors.primary, borderColor: look.colors.secondary }} />
      <span class="wc-bar-name">
        <span class="wc-bar-label">{look.name}</span>
        {isSelf ? <span class="wc-bar-tag">You</span> : null}
        {isRival ? <span class="wc-bar-tag wc-bar-tag--rival">Rival</span> : null}
      </span>
      <span class="wc-bar-track">
        <span class="wc-bar-fill" style={{ width: `${Math.max(1.5, barFill(track, entry.progress, max) * 100)}%`, background: look.colors.primary }} />
      </span>
      <span class="wc-bar-val wc-num">
        {age.stale.length > 0 ? (
          <span class="wc-bar-incl wc-year" title={inclText(age.stale)}>
            {inclYears(age.stale)}
          </span>
        ) : null}
        {fmtTrack(track, entry.progress)}
      </span>
      <SourceChip ids={entry.sources} year={age.stale.length > 0 ? undefined : entry.dataYear} variant="mini" />
    </div>
  );
}

function BarTip({ track, entry, name }: { track: VictoryTrack; entry: VictoryEntry; name: string }) {
  const { world, state } = useServices();
  const at = state.date.value;
  const next = track === 'space' ? spaceLadderOf(world, entry.iso3)?.next : null;
  return (
    <>
      <div class="wc-tip-title">{name}</div>
      <div class="wc-tip-big wc-num">{fmtTrack(track, entry.progress)}</div>
      <div class="wc-tip-detail">
        {partsOf(entry.detail).map((p, i) => (
          <Fragment key={p.text}>
            {i > 0 ? PART_SEP : ''}
            <span class={p.year !== null && isStale(p.year, at) ? 'wc-stale' : ''}>{p.text}</span>
          </Fragment>
        ))}
      </div>
      {next ? (
        <div class="wc-tip-row">
          <span>Next rung</span>
          <b>{next.label}</b>
        </div>
      ) : null}
      <div class="wc-tip-row">
        <span>Oldest input</span>
        <b class={isStale(entry.dataYear, at) ? 'wc-stale' : ''}>{entry.dataYear}</b>
      </div>
      {entry.sources.slice(0, TIP_SOURCES).map((id) => (
        <div class="wc-tip-src" key={id}>
          <b>{world.source(id)?.publisher ?? id}</b>
          <span>{world.source(id)?.title ?? ''}</span>
        </div>
      ))}
    </>
  );
}

// Small-multiple line chart, 2000 → the viewed date. Every series in its nation's colour: you thick with a
// dark halo, an end dot and an end label; rivals thin; the track leader (when someone else)
// dashed. Y labels sit in a gutter left of the plot; faint verticals mark every 5 years.
//
//   14.8% ┤    ╱‾‾‾‾‾‾‾‾‾‾╲___●  You 11.0%
//         │ ‾‾‾╲____╱‾‾‾‾‾‾‾‾‾‾‾
//      0% ┤ :    :    :    :    :
//          2000                2026
function HistoryChart({ track, data, self, rivals, color }: { track: VictoryTrack; data: TrackData; self: string; rivals: string[]; color: string }) {
  const { world } = useServices();
  const { series } = data;
  if (series === null) {
    return <div class="wc-vic-chart wc-vic-chart--wait" aria-busy="true">History loading…</div>;
  }
  if (series.length < 2) {
    return null;
  }

  const leader = data.entries[0]?.iso3;
  const lines = [...rivals, self];
  if (leader && !lines.includes(leader)) {
    lines.unshift(leader);
  }

  let max = 0;
  for (const p of series) {
    for (const e of p.entries) {
      if (lines.includes(e.iso3)) {
        max = Math.max(max, e.progress);
      }
    }
  }
  const top = max > 0 ? max * CHART_HEADROOM : 1;

  const x0 = series[0].year;
  const x1 = series[series.length - 1].year;
  const span = x1 - x0 || 1;
  const xOf = (year: number) => ((year - x0) / span) * CHART_W;
  const yOf = (v: number) => CHART_H - (v / top) * CHART_H;

  const pathOf = (iso3: string): string => {
    let d = '';
    for (const p of series) {
      const e = p.entries.find((q) => q.iso3 === iso3);
      if (!e) {
        continue;
      }
      d += `${d ? 'L' : 'M'}${xOf(p.year).toFixed(1)} ${yOf(e.progress).toFixed(1)}`;
    }
    return d;
  };

  const isLeaderOnly = (iso3: string) => iso3 === leader && iso3 !== self && !rivals.includes(iso3);
  const paths: JSX.Element[] = [];
  for (const iso3 of lines) {
    const d = pathOf(iso3);
    const stroke = lookOf(world, iso3).colors.primary;
    if (iso3 === self) {
      paths.push(<path key="halo" d={d} fill="none" stroke="rgba(0,0,0,.6)" stroke-width="5" stroke-linejoin="round" vector-effect="non-scaling-stroke" />);
    }
    paths.push(
      <path
        key={iso3}
        d={d}
        fill="none"
        stroke={stroke}
        stroke-width={iso3 === self ? 3 : 1.6}
        stroke-dasharray={isLeaderOnly(iso3) ? '4 3' : undefined}
        stroke-linejoin="round"
        vector-effect="non-scaling-stroke"
      />,
    );
  }

  const grid: JSX.Element[] = [];
  for (let y = Math.ceil(x0 / GRID_YEARS) * GRID_YEARS; y <= x1; y += GRID_YEARS) {
    grid.push(<line key={y} x1={xOf(y)} x2={xOf(y)} y1="0" y2={CHART_H} class="wc-vic-grid" vector-effect="non-scaling-stroke" />);
  }

  // Your latest point, for the end dot and label (percent of the plot box).
  const lastSelf = [...series].reverse().find((p) => p.entries.some((e) => e.iso3 === self));
  const selfEntry = lastSelf?.entries.find((e) => e.iso3 === self);
  const selfColour = lookOf(world, self).colors.primary;

  const legend = lines.map((iso3) => {
    const look = lookOf(world, iso3);
    const dashed = isLeaderOnly(iso3);
    return (
      <span key={iso3} class={iso3 === self ? 'wc-vic-key--self' : ''}>
        <i class={dashed ? 'wc-dash' : ''} style={dashed ? { borderColor: look.colors.primary } : { background: look.colors.primary }} />
        {iso3 === self ? 'You' : look.name}
      </span>
    );
  });

  return (
    <div class="wc-vic-chart">
      <div class="wc-vic-plot">
        <span class="wc-vic-y wc-num">
          <span>{fmtTrack(track, max)}</span>
          <span>{trackZero(track)}</span>
        </span>
        <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} preserveAspectRatio="none" aria-label="History since 2000">
          <defs>
            <linearGradient id={`wc-vic-area-${color.slice(1)}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stop-color={selfColour} stop-opacity="0.22" />
              <stop offset="1" stop-color={selfColour} stop-opacity="0" />
            </linearGradient>
          </defs>
          {grid}
          <line x1="0" x2={CHART_W} y1={yOf(max)} y2={yOf(max)} class="wc-vic-grid wc-vic-grid--max" vector-effect="non-scaling-stroke" />
          <path d={`${pathOf(self)}L${CHART_W} ${CHART_H}L0 ${CHART_H}Z`} fill={`url(#wc-vic-area-${color.slice(1)})`} />
          {paths}
        </svg>
        {lastSelf && selfEntry ? (
          <span
            class="wc-vic-end"
            style={{ left: `${(xOf(lastSelf.year) / CHART_W) * 100}%`, top: `${(yOf(selfEntry.progress) / CHART_H) * 100}%`, '--c': selfColour }}
          >
            <i />
            <b class="wc-num">You {fmtTrack(track, selfEntry.progress)}</b>
          </span>
        ) : null}
      </div>
      <div class="wc-vic-axis">
        <span>{x0}</span>
        <span class="wc-vic-legend">{legend}</span>
        <span>{x1}</span>
      </div>
    </div>
  );
}

function WeightsEditor() {
  const set = (t: BaseTrack, v: number) => {
    weights.value = { ...weights.value, [t]: v };
    saveWeights(weights.value);
  };
  return (
    <div class="wc-weights">
      {BASE_TRACKS.map((t) => (
        <label key={t} class="wc-weight">
          <span class="wc-weight-name">
            <Icon name={TRACK_META[t].icon} size={14} />
            {TRACK_META[t].name}
          </span>
          <input
            type="range"
            min={0}
            max={WEIGHT_MAX}
            step={0.5}
            value={weights.value[t]}
            onInput={(e) => set(t, Number((e.currentTarget as HTMLInputElement).value))}
          />
          <span class="wc-weight-val wc-num">{weights.value[t].toFixed(1)}</span>
        </label>
      ))}
      <button
        type="button"
        class="wc-weights-reset"
        onClick={() => {
          weights.value = { ...DEFAULT_WEIGHTS };
          saveWeights(weights.value);
        }}
      >
        Reset to equal weights
      </button>
    </div>
  );
}
